<template>
  <div
    class="AIAssistantConversationsListItem d-flex align-center justify-space-between rounded"
    :class="{'active' : isActiveConversation}"
  >
    <AppTooltip location="top" max-width="320" class="AIAssistant__tooltip flex-grow-1 min-width-0">
      <template #activator="{ props }">
        <button
          class="AIAssistantConversationsListItem__select d-flex align-center justify-space-between px-3 py-2"
          data-locator="conversation-item"
          :disabled="isUnavailable"
          v-bind="props"
          @click="handleSelectConversation"
        >
          <span class="text-truncate" :class="isUnavailable && 'text-grey-lighten-1'">{{ conversation.title || $t(`common.untitled`) }}</span>
          <span class="text--x-small flex-shrink-0 text-grey-darken-2">{{ relativeTime }}</span>
        </button>
      </template>
      <span v-if="!isUnavailable">{{ conversation.title || $t(`common.untitled`) }}</span>
      <template v-else>
        <span>{{ $t('common.ai-assistant.AIAssistantConversationsListItem.conversationUnavailableTooltipMainHint', { projectName: conversation?.room?.name }) }}</span>
        <p>{{ $t('common.ai-assistant.AIAssistantConversationsListItem.conversationUnavailableTooltipDescription') }}</p>
      </template>
    </AppTooltip>

    <AppTooltip v-if="shouldShowDeleteButton" location="top" max-width="260">
      <template #activator="{ props }">
        <span
          class="AIAssistantConversationsListItem__delete-wrapper"
          data-locator="delete-conversation-action"
          v-bind="props"
        >
          <app-button
            class="AIAssistantConversationsListItem__delete d-flex align-center justify-center rounded"
            data-locator="delete-conversation-button"
            type="icon"
            size="medium"
            :disabled="isDeleteDisabled"
            @click.stop="$emit('request-delete', conversation.id)"
          >
            <app-material-icon class="AIAssistantConversationsListItem__delete-icon" name="delete" :size="18" />
          </app-button>
        </span>
      </template>
      <span>{{ deleteTooltip }}</span>
    </AppTooltip>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapGetters, mapMutations, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { ISOToShortenedDate, ISOToWeekDay } from '@/common/utils/dates'
import { AIAssistantMutationTypes } from '@/store/modules/ai-assistant/mutation_types'
import { AIAssistantQuestionTypes, type AIAssistantConversation } from '@/types/ai-assistant'


const MINUTES_IN_DAY = 1440 // 24 * 60
const MINUTES_IN_WEEK = 10080 // 7 * 24 * 60

export default defineComponent({
  name: 'AIAssistantConversationsListItem',
  components: {
    AppTooltip,
  },
  emits: ['request-delete'],
  props: {
    conversation: {
      type: Object as () => AIAssistantConversation,
      required: true,
    },
  },
  computed: {
    ...mapState('aiAssistant', ['conversationActiveId']),
    ...mapGetters('user', ['isProtegeAskDocFreemiumEnabled']),
    ...mapGetters('room', ['roomMnemo']),
    isActiveConversation(): boolean {
      return this.conversation.id === this.conversationActiveId
    },
    isUnavailable(): boolean {
      return this.conversation.type === AIAssistantQuestionTypes.ASK_DOC &&
        this.conversation?.room?.mnemo !== this.roomMnemo
    },
    isDeleteDisabled(): boolean {
      return this.isProtegeAskDocFreemiumEnabled
    },
    shouldShowDeleteButton(): boolean {
      return !this.isUnavailable
    },
    deleteTooltip(): string {
      if (this.isProtegeAskDocFreemiumEnabled) {
        return this.$t('common.ai-assistant.AIAssistantConversationsListItem.deletePreviewTooltip')
      }
      return this.$t('common.ai-assistant.AIAssistantConversationsListItem.deleteTooltip')
    },
    relativeTime(): string {
      const now = new Date()
      const updatedAt = new Date(this.conversation.updatedAt || now)
      const diffInMinutes = Math.floor((now.getTime() - updatedAt.getTime()) / 60000)

      if (diffInMinutes < 1) {
        return this.$t('common.now')
      }
      
      if (diffInMinutes < MINUTES_IN_DAY) {
        const hours = updatedAt.getHours().toString().padStart(2, '0')
        const minutes = updatedAt.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }
      
      if (diffInMinutes < MINUTES_IN_WEEK) {
        return ISOToWeekDay(this.conversation.updatedAt || '')
      }
      
      return ISOToShortenedDate(this.conversation.updatedAt || '')
    },
  },
  methods: {
    ...mapMutations('aiAssistant', [
      AIAssistantMutationTypes.SET_CONVERSATION_ACTIVE_ID,
      AIAssistantMutationTypes.SET_QUESTION_TYPE,
      AIAssistantMutationTypes.SET_SELECTED_DOCUMENTS,
    ]),
    handleSelectConversation () {
      if (this.conversation.type === AIAssistantQuestionTypes.ASK_DOC) {
        const files = (this.conversation.files ?? []).map(({ id, name }) => ({ id, name, type: 'file' as const }))
        this.SET_SELECTED_DOCUMENTS(files)
      } else {
        this.SET_SELECTED_DOCUMENTS([])
      }
      this.SET_CONVERSATION_ACTIVE_ID(this.conversation.id)
      this.SET_QUESTION_TYPE(this.conversation.type)
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.AIAssistantConversationsListItem {
  min-height: 40px;
  width: 100%;

  &:hover {
    background-color: rgb(var(--v-theme-grey-50));
    text-align: left;
  }

  &:hover &__delete-wrapper {
    opacity: 1;
    pointer-events: auto;
  }

  &.active {
    background-color: #DDD7F5;
  }

  &.active &__delete:not(:disabled):hover {
    background-color: #D2CDEB;
  }

  &__select {
    column-gap: 0.5rem;
    min-height: 40px;
    text-align: left;
    width: 100%;
  }

  &__delete {
    @include border-radius('border-radius-2');
    color: rgb(var(--v-theme-grey-darken-2));
    height: 28px;
    margin-right: 4px;
    opacity: 1;
    padding: 4px;
    width: 28px;

    &:not(:disabled):hover {
      background-color: #E6E8EB;
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-lighten-1));
      cursor: not-allowed;
    }
  }

  &__delete-icon {
    pointer-events: none;
  }

  &__delete-wrapper {
    display: flex;
    opacity: 0;
    pointer-events: none;
  }
}
</style>
